import React, {useEffect, useRef, useState} from 'react'
import {useSelector} from 'react-redux';
import {Link, useNavigate, useParams} from 'react-router-dom';
import {useAppDispatch} from '../../store';
import {
    fetchAllOffers,
    fetchAllWidgets,
    fetchAppList,
    fetchBusinessTypes,
    fetchCategories,
    fetchFeatureApps,
    fetchTopApps,
    fetchTrendingApps
} from '../../store/actions';
import Auth from '../../models/response/auth.model';
import {SingleAppModel} from './../single-app/models/response/single-app.model';
import {AppWizardModel, FeatureAppModel} from './../discover/models/response/app-wizard.model';
import {CategoryListModel, CategoryModel} from './models/response/app-category.model';
import BusinessType1 from '../../assets/images/business-type-wiz-feature-1.svg';
import {BusinessType} from './components/BusinessType';
import {BusinessTypes} from './models/response/business-types.model';
import * as CONSTANTS from "../../constants";
import SearchBanner from './components/SearchBanner';

type Props = {
    auth: Auth
}

const AppWizard = ({auth}: Props) => {
    const {wuuid} = useParams();

    const dropdownRef = useRef<HTMLDivElement>(null);

    const [appDetails, setAppDetails] = useState<SingleAppModel | null>(null);
    const [featureAppDetails, setFeatureAppDetails] = useState<FeatureAppModel | null>(null);
    const [categoryItems, setCategoryItems] = useState<CategoryListModel | null>(null);
    const [allAppDetails, setAllAppDetails] = useState<FeatureAppModel | null>(null);
    const [topAppsOffset, setTopAppsOffset] = useState<number>(0);
    const [topAppsLimit, setTopAppsLimit] = useState<number>(4);
    const [newAppsOffset, setNewAppsOffset] = useState<number>(0);
    const [newAppsLimit, setNewAppsLimit] = useState<number>(9);
    const [showSections, setShowSections] = useState(false);
    const [beforeRegDate, setBeforeRegDate] = useState<Date>(new Date("2023-11-28"));
    const [featuredAppsOffset, setFeaturedAppsOffset] = useState<number>(0);
    const [featuredAppsLimit, setFeaturedAppsLimit] = useState<number>(8);
    const [categoriesOffset, setCategoriesOffset] = useState<number>(0);
    const [categoriesLimit, setCategoriesLimit] = useState<number>(8);
    const [allAppsOffset, setAllAppsOffset] = useState<number>(0);
    const [allAppsLimit, setAllAppsLimit] = useState<number>(9);
    const [hasSearchResults, setHasSearchResults] = useState(false);

    const dispatch = useAppDispatch();

    const {
        featureAppListDetails,
        categoryList,
        loading,
        topAppsList,
        newApps,
        trendingApps,
        businessTypes,
        allApps,
        searchResults
    } = useSelector<any, any>(({appWizard}) => appWizard);

    const {userDetails} = useSelector<any, any>(({user}) => user);

    const {
        authData
    } = useSelector<any, any>(({authentication}) => authentication);

    const {
        allOffersList
    } = useSelector<any, any>(({offers}) => offers);

    const {
        appList
    } = useSelector<any, any>(({settings}) => settings);

    const navigate = useNavigate();

    const tenantGroupId = (userDetails && userDetails.OwnedEnterprises[0]) ? userDetails.OwnedEnterprises[0].groupId : "";

    const onRedirect = (uuid: string | null) => {
        if (auth && auth.accessToken !== '') {
            navigate(`/discover/widget/${uuid}`);
        } else {
            navigate(`/widget/${uuid}`);
        }
    }

    const onRedirectTopApps = () => {
        if (auth && auth.accessToken !== '') {
            navigate(`/discover/apps/top`);
        } else {
            navigate(`/apps/top`);
        }
    }

    const onRedirectAllApps = () => {
        if (auth && auth.accessToken !== '') {
            navigate(`/discover/apps/all`);
        } else {
            navigate(`/apps/all`);
        }
    }

    const onRedirectTopCategories = () => {
        if (auth && auth.accessToken !== '') {
            navigate(`/discover/apps/categories`);
        } else {
            navigate(`/apps/categories`);
        }
    }

    const redirectToApp = (uuid: string | null) => {
        // console.log('Auth ***** ', auth)
        if (auth && auth.accessToken !== '') {
            return `/discover/widget/${uuid}`;
        } else {
            return `/widget/${uuid}`;
        }
    }

    const redirectToCategories = (uuid: string | null) => {
        // console.log('Auth ***** ', auth)
        if (auth && auth.accessToken !== '') {
            return `/discover/apps/${uuid}`;
        } else {
            return `/apps/${uuid}`;
        }
    }

    const redirectToAll = (uuid: string | null) => {
        // console.log('Auth ***** ', auth)
        if (auth && auth.accessToken !== '') {
            return `/discover/widget/${uuid}`;
        } else {
            return `/widget/${uuid}`;
        }
    }

    const handleSearchSubmit = (query: string) => {
        setHasSearchResults(!!query);
    }

    const handleDynamicResults = (hasResults: boolean) => {
    }

    useEffect(() => {
        dispatch(fetchFeatureApps(featuredAppsLimit, featuredAppsOffset));
        dispatch(fetchTopApps(topAppsLimit, topAppsOffset));
        // dispatch(fetchNewlyArrivedApps(newAppsLimit, newAppsOffset));
        dispatch(fetchCategories());
        dispatch(fetchTrendingApps());
        dispatch(fetchBusinessTypes());
        dispatch(fetchAllWidgets(allAppsLimit, allAppsOffset))
    }, []);

    useEffect(() => {
        setAppDetails(appDetails)
    });

    useEffect(() => {
        if (featureAppListDetails) {
            setFeatureAppDetails(featureAppListDetails)
        }
    }, [featureAppListDetails])

    useEffect(() => {
        if (categoryList) {
            setCategoryItems(categoryList)
        }
    }, [categoryList])

    useEffect(() => {
        if (allApps) {
            setAllAppDetails(allApps)
        }
    }, [allApps])

    useEffect(() => {
        let route = window.location.pathname.split('/')[1];
        if (route == 'apps' && authData) {
            navigate('/discover/apps/wizard');
        }
    }, []);

    useEffect(() => {
        setShowSections(true)
    });

    useEffect(() => {
        const tenantGroupId = (userDetails && userDetails?.OwnedEnterprises[0]) ? userDetails?.OwnedEnterprises[0].groupId : "";
        if (userDetails) {
            dispatch(fetchAllOffers(userDetails?.uuid, tenantGroupId));
        } else {
            dispatch(fetchAllOffers("0", "0"));
        }
    }, [userDetails]);

    useEffect(() => {
        const tenantGroupId = (userDetails && userDetails.OwnedEnterprises[0]) ? userDetails.OwnedEnterprises[0].groupId : "";
        if (tenantGroupId !== "") {
            dispatch<any>(fetchAppList(tenantGroupId, '0', 20, 0, appList));
        }
    }, [userDetails]);

    function startSlider() {
        const slides = document.querySelectorAll('.sliderItem');
        if (slides.length === 0) return;

        let currentSlide = 0;


        slides.forEach((slide, index) => {
            const slideElement = slide as HTMLElement;
            if (index !== 0) {
                slideElement.style.opacity = '0';
                slideElement.style.display = 'none';
            } else {
                slideElement.style.opacity = '1';
                slideElement.style.display = 'block';
            }
        });


        setInterval(() => {

            const currentElement = slides[currentSlide] as HTMLElement;
            currentElement.style.opacity = '0';

            setTimeout(() => {
                currentElement.style.display = 'none';

                // Move to next slide
                currentSlide = (currentSlide + 1) % slides.length;

                // Show next slide
                const nextElement = slides[currentSlide] as HTMLElement;
                nextElement.style.display = 'block';

                setTimeout(() => {
                    nextElement.style.opacity = '1';
                }, 50);
            }, 500); // Fade out duration
        }, 30000); // Change slide every 30 seconds
    }

    useEffect(() => {
        startSlider();
    }, []);

    return (
        <div className="main-content main-spacer-x-y">
            <div className="top-feature-slider__wrapper">
                <div className="top-feature-slider__inner">
                    <div className="main-slider" aria-label="gallery">
                        <div className="slider-item sliderItem fading"
                             style={{backgroundImage: `url(${require('../../assets/images/main-slider-5.jpg')})`}}>
                        </div>

                        <div className="slider-item sliderItem fading"
                             style={{backgroundImage: `url(${require('../../assets/images/main-slider-6.jpg')})`}}>
                        </div>
                    </div>
                </div>
            </div>

            <SearchBanner
                auth={auth}
                onSearchSubmit={handleSearchSubmit}
                onDynamicResults={handleDynamicResults}
            />

            {userDetails && userDetails.createdAt && (new Date(userDetails.createdAt) < new Date(beforeRegDate)) &&
                <>
                    <div className="alert warning">
                        <span className="alert-text__wrapper">
                            <i className="alert-icon bi bi-exclamation-circle-fill"/>
                            <span
                                className="alert-text">{"To view other applications, please visit the old Marketplace."}</span>
                            <button className="btn btn-small secondary-action" onClick={() => {
                                window.location.replace(`${CONSTANTS.REACT_APP_CLASSIC_UI_URL}`);
                            }}>Switch to classic view</button>
                        </span>
                    </div>
                    <br></br></>
            }

            {hasSearchResults &&
                <>
                    <div className="app-listing-section__wrapper type-1">
                        <div className="app-listing-section-title__wrapper">
                            <span>Search Results</span>
                        </div>
                        <div className="app-listing-section not-at-right type-1-inner" id="listingScrollDiv">
                            {searchResults && Array.isArray(searchResults) && searchResults.length > 0 ? searchResults.map((item: any) => {
                                    return (
                                        <div className="app-listing-item__wrapper" onClick={() => onRedirect(item.uuid)}>
                                            <a>
                                                <div className="app-listinng-item">
                                                    <div className="app-item-content__wrapper">
                                                        <div className="card app-thumbnail-holder"
                                                             style={{backgroundImage: `url(${item.thumbnail})`}}>
                                                        </div>
                                                    </div>
                                                    <div className="app-item-content__wrapper app-details">
                                                        <div className="card app-media-holder"
                                                             style={{backgroundImage: `url(${item.icon})`}}>
                                                        </div>
                                                        <div className="app-media-info-holder">
                                                            {/* <Link to={`/widget/${value.uuid}`} className="app-media-title">{value.title}</Link> */}
                                                            <Link to={redirectToApp(item.uuid)}
                                                                  className="app-media-title">{item.title}</Link>
                                                            <Link to={redirectToCategories(item.Category.uuid)}
                                                                  className="app-media-category">{item.Category.title} </Link>
                                                            <div className="app-stats__wrapper">
                                                                <ul className="app-stats-list">
                                                                    {/* <li className="app-stats-item"><span className="stats-icon"><i className="bi bi-download" /></span><span className="stats-value">200+</span></li> */}
                                                                    {/* <li className="app-stats-item"><span className="stats-divider"><i className="bi bi-dot" /></span></li> */}
                                                                    {/* <li className="app-stats-item"><span className="stats-icon ico-highlighter"><i className="bi bi-star-fill" /></span><span className="stats-value">5.0</span></li> */}
                                                                </ul>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </a>
                                        </div>
                                    )
                                })
                                :
                                <>
                                    <div className="panel-table-content-list">
                                        <div className="empty-stack__wrapper">
                                            {/* <img src={""} alt="" /> */}
                                            <h5>No results found</h5>
                                        </div>
                                    </div>
                                </>}
                        </div>
                    </div>
                </>
            }
            {!hasSearchResults &&
                <>

                    <div className="app-listing-section__wrapper type-1">
                        <div className="app-listing-section-title__wrapper">
                            <span>Featured Apps</span>
                        </div>
                        <div className="app-listing-section not-at-right type-1-inner" id="listingScrollDiv">
                            {featureAppDetails && Array.isArray(featureAppDetails) && featureAppDetails.map((value: AppWizardModel, index: number) => {
                                const hasOffer = Array.isArray(allOffersList?.allOffersWidgetDetailsList) && allOffersList?.allOffersWidgetDetailsList.some((offer: {
                                    uuid: string | null;
                                }) => offer.uuid === value.uuid);
                                const hasSubscribed = Array.isArray(appList) && appList.some((appId: {
                                    uuid: string | null;
                                }) => appId.uuid === value.uuid);
                                return (
                                    <div className="app-listing-item__wrapper" key={index}
                                         onClick={() => onRedirect(value.uuid)}>
                                        <a>
                                            <div className="app-listinng-item">
                                                <div className="app-item-content__wrapper">
                                                    <div className="card app-thumbnail-holder"
                                                         style={{backgroundImage: `url(${value.thumbnail})`}}>
                                                        {userDetails ? (
                                                            hasOffer && !hasSubscribed &&
                                                            <div className="discount-label">Offer</div>
                                                        ) : <></>
                                                        }
                                                    </div>
                                                </div>
                                                <div className="app-item-content__wrapper app-details">
                                                    <div className="card app-media-holder"
                                                         style={{backgroundImage: `url(${value.icon})`}}>
                                                    </div>
                                                    <div className="app-media-info-holder">
                                                        <Link to={redirectToApp(value.uuid)}
                                                              className="app-media-title">{value.title}</Link>
                                                        <Link to={redirectToCategories(value.Category.uuid)}
                                                              className="app-media-category">{value.Category.title}</Link>
                                                        <div className="app-stats__wrapper">
                                                            <ul className="app-stats-list">
                                                                {/* Additional stats can be added here */}
                                                            </ul>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </a>
                                    </div>
                                );
                            })}

                        </div>
                        {/* Disable view more apps button in Discover page - MW- 8414 */}
                        {/* <div className="app-listing-section-footer">
                    <button className="btn btn-primary" onClick={() => navigate("/apps/new")}>View more apps</button>
                </div> */}
                    </div>
                </>
            }

            {showSections &&
                <div className="app-listing-section__wrapper type-2">
                    <div className="app-listing-section-title__wrapper">
                        <span>Top Apps</span>
                    </div>
                    <div className="app-listing-section not-at-right type-1-inner">
                        {Array.isArray(topAppsList) && topAppsList.map((value: AppWizardModel, index: number) => {
                            const hasOffer = Array.isArray(allOffersList?.allOffersWidgetDetailsList) && allOffersList?.allOffersWidgetDetailsList.some((offer: {
                                uuid: string | null;
                            }) => offer.uuid === value.uuid);
                            const hasSubscribed = Array.isArray(appList) && appList.some((appId: {
                                uuid: string | null;
                            }) => appId.uuid === value.uuid);
                            return (
                                <div className="app-listing-item__wrapper" key={index}
                                     onClick={() => onRedirect(value.uuid)}>
                                    <a>
                                        <div className="app-listinng-item">
                                            <div className="app-item-content__wrapper app-details">
                                                <div className="card app-media-holder"
                                                     style={{backgroundImage: `url(${value.thumbnail})`}}>
                                                    {userDetails ? (
                                                        hasOffer && !hasSubscribed &&
                                                        <div className="discount-label">Offer</div>
                                                    ) : <></>
                                                    }
                                                </div>
                                                <div className="app-media-info-holder">
                                                    <Link to={redirectToApp(value.uuid)}
                                                          className="app-media-title">{value.title}</Link>
                                                    <p className="app-media-desc">{value.description}</p>
                                                    {/* <Link to={`/app-category/${value.Category.uuid}`} className="app-media-category">{value.Category.title} </Link> */}
                                                    <Link to={redirectToCategories(value.Category.uuid)}
                                                          className="app-media-category">{value.Category.title} </Link>
                                                    <div className="app-stats__wrapper">
                                                        <ul className="app-stats-list">
                                                            {/* <li className="app-stats-item"><span className="stats-icon"><i className="bi bi-download" /></span><span className="stats-value">400+</span></li>
                                                        <li className="app-stats-item"><span className="stats-divider"><i className="bi bi-dot" /></span></li>
                                                        <li className="app-stats-item"><span className="stats-icon ico-highlighter"><i className="bi bi-star-fill" /></span><span className="stats-value">4.8</span></li> */}
                                                        </ul>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </a>
                                </div>
                            )
                        })}
                    </div>
                    {/* <div className="app-listing-section-footer">
                        <button className="btn btn-primary" onClick={() => onRedirectTopApps()}>View more apps</button>
                    </div> */}
                </div>}

            {
                showSections &&
                <div className="app-listing-section__wrapper type-2">
                    <div className="app-listing-section-title__wrapper">
                        <span>All Apps</span>
                    </div>
                    <div className="app-listing-section not-at-right type-2-inner">
                        {allAppDetails && allAppDetails && allAppDetails.map((value: AppWizardModel, index: number) => {
                            const hasOffer = Array.isArray(allOffersList?.allOffersWidgetDetailsList) && allOffersList?.allOffersWidgetDetailsList.some((offer: {
                                uuid: string | null;
                            }) => offer.uuid === value.uuid);
                            const hasSubscribed = Array.isArray(appList) && appList.some((appId: {
                                uuid: string | null;
                            }) => appId.uuid === value.uuid);
                            return (
                                <div className="app-listing-item__wrapper" key={index}
                                     onClick={() => onRedirect(value.uuid)}>
                                    <a>
                                        <div className="app-listinng-item">
                                            <div className="app-item-content__wrapper app-details">
                                                <div className="card app-media-holder"
                                                     style={{backgroundImage: `url(${value.thumbnail})`}}>
                                                    {userDetails ? (
                                                        hasOffer && !hasSubscribed &&
                                                        <div className="discount-label">Offer</div>
                                                    ) : <></>
                                                    }
                                                </div>
                                                <div className="app-media-info-holder">
                                                    <Link to={redirectToAll(value.uuid)}
                                                          className="app-media-title">{value.title}</Link>
                                                    <p className="app-media-desc">{value.description}</p>
                                                    <Link to={redirectToCategories(value.Category.uuid)}
                                                          className="app-media-category">{value.Category.title} </Link>
                                                    <div className="app-stats__wrapper">
                                                        <ul className="app-stats-list">

                                                        </ul>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </a>
                                </div>
                            )
                        })}
                    </div>
                    <div className="app-listing-section-footer">
                        <button className="btn btn-primary" onClick={() => navigate("/apps/all")}>View more apps
                        </button>
                    </div>
                </div>
            }

            {/* { showSections &&
            <div className="app-listing-section__wrapper type-2">
                <div className="app-listing-section-title__wrapper">
                    <span>Trending Apps</span>
                </div>
                <div className="app-listing-section not-at-right type-2-inner">
                    {Array.isArray(trendingApps) && trendingApps.map((value: AppWizardModel, index: number) => {
                        return (
                            <div className="app-listing-item__wrapper" key={index} onClick={() => onRedirect(value.uuid)}>
                                <a>
                                    <div className="app-listinng-item">
                                        <div className="app-item-content__wrapper app-details">
                                            <div className="card app-media-holder"
                                                style={{ backgroundImage: `url(${value.thumbnail})` }}>
                                            </div>
                                            <div className="app-media-info-holder">
                                                <Link to={redirectToApp(value.uuid)} className="app-media-title">{value.title}</Link>
                                                <p className="app-media-desc">{value.description}</p>
                                                <Link to={redirectToCategories(value.Category.uuid)} className="app-media-category">{value.Category.title} </Link>
                                                <div className="app-stats__wrapper">
                                                    <ul className="app-stats-list">
                                                    </ul>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </a>
                            </div>
                        )
                    })}
                </div>
            </div>
        } */}
            <div className="listing-slider-carousel with-titles">
                <div className="listing-slider-section-title__wrapper">
                    <span>Apps by categories</span>
                </div>
                <div className="listing-slider-control-icons ">
                    <button className="btn"><i className="bi bi-chevron-left"/></button>
                    <button className="btn"><i className="bi bi-chevron-right"/></button>
                </div>
                <div className="listing-slider__wrapper dragscroll">
                    {categoryItems && categoryItems.data && categoryItems.data.map((value: CategoryModel, index: number) => {
                        return (
                            <div className="slider-listing-item__wrapper"
                                 onClick={() => navigate(`/app-category/${value.uuid}`)} key={index}>
                                <a>
                                    <div className="slider-listing-item">
                                        <div className="slider-item-content__wrapper">
                                            <div className="slider-item-content__wrapper">
                                                <div className="card slider-thumbnail-holder" style={{
                                                    backgroundSize: "40%",
                                                    backgroundRepeat: "no-repeat",
                                                    borderRadius: "8px 0px",
                                                    opacity: "60%",
                                                    backgroundImage: value.thumbnail ? `url(https://d2xt8w8fh2bnjt.cloudfront.net/qa/categories/${value.thumbnail})` : ''
                                                }}>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="slider-item-content__wrapper">
                                            <div className="slider-media-info-holder" style={{
                                                color: "black",
                                                backgroundColor: "#457ede4d",
                                                width: "100%",
                                                padding: "6px",
                                                backgroundClip: "initial",
                                                borderRadius: "0px 0px 8px 8px",
                                                boxShadow: "0 4px 12px #0000001a",
                                                display: "flex",
                                                flexDirection: "column",
                                                minWidth: "100%",
                                                marginTop: "0px",
                                                paddingLeft: "12px"
                                            }}>
                                                <a className="slider-media-title">{value.title}</a>
                                            </div>
                                        </div>
                                    </div>
                                </a>
                            </div>
                        )
                    })}
                </div>
                <div className="listing-slider-section-footer">
                    <button className="btn btn-primary" onClick={() => onRedirectTopCategories()}>View all categories
                    </button>
                </div>
            </div>
            <div className="feature-inner-section-wrapper center bg-primary-gradient-overlay__wrapper">
                <div className="feature-inner-section-inner bg-primary-gradient-overlay__inner">
                    <div className="top-content__wrapper">
                        <h2 className="title">What Type of Business Do You Run?</h2>
                        <p>
                            Explore Available Solutions for Your Business Type with Our Assistance.
                        </p>
                    </div>
                    <div className="feature-inner-section-lister__wrapper">
                        {Array.isArray(businessTypes) && businessTypes.length > 0 && businessTypes.map((value: BusinessTypes, index: number) => {
                            return (
                                <BusinessType
                                    backgroundImage={BusinessType1}
                                    title={value.businessType}
                                    description={value.description}
                                    uuid={value.uuid}
                                />
                            )
                        })}
                    </div>
                </div>
            </div>

        </div>
    )
}

export default AppWizard
